import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { getAuthUser } from '../../../lib/services/auth.service';
import { hasAdminAccess } from '../../../lib/services/user.service';
import { useLogQueryError } from '../../../lib/hooks/use-log-query-error';
import { PlayerScoresContainer } from './player-scores.container';


// `hasScores` keeps the picker to sessions this player was actually scored in —
// the roster includes future and unscored ones, which have nothing to show here.
const GET_PLAYER_SCORE_SESSIONS = gql`
  query GetPlayerScoreSessions($playerAssessmentId: ID!) {
    playerAssessmentSessions(playerAssessmentId: $playerAssessmentId, hasScores: true) {
      _id
      date
      start
    }
  }
`;

const GET_PLAYER_SCORES = gql`
  query GetPlayerScores($companyId: ID!, $assessmentSessionId: ID!, $playerAssessmentId: ID!) {
    drills(companyId: $companyId, includeGlobal: true) {
      _id
      name
    }
    skills(companyId: $companyId, includeGlobal: true, includeDeleted: true) {
      _id
      name
    }
    assessmentSessionScores(assessmentSessionId: $assessmentSessionId, playerAssessmentId: $playerAssessmentId) {
      _id
      raw
      type
      drillId
      skillId
      userId
      round
    }
    allMembers(companyId: $companyId) {
      _id
      user {
        _id
        name
      }
    }
  }
`;

// Presence-only counterpart to GET_PLAYER_SCORES: which evaluator submitted for which
// event, with no score values. Skills aren't selected — nothing renders them here.
const GET_PLAYER_SCORE_SUBMISSIONS = gql`
  query GetPlayerScoreSubmissions($companyId: ID!, $assessmentSessionId: ID!, $playerAssessmentId: ID!) {
    drills(companyId: $companyId, includeGlobal: true) {
      _id
      name
    }
    assessmentSessionScoreSubmissions(assessmentSessionId: $assessmentSessionId, playerAssessmentId: $playerAssessmentId) {
      drillId
      userId
    }
    allMembers(companyId: $companyId) {
      _id
      user {
        _id
        name
      }
    }
  }
`;

interface IPlayerScoresGraphQL {
  playerAssessmentId: string;
}

export const PlayerScoresGraphQL: React.FC<IPlayerScoresGraphQL> = ({ playerAssessmentId }) => {
  const [activeAssessmentSessionId, setActiveAssessmentSessionId] = useState();
  const { data, error: sessionsError } = useQuery(GET_PLAYER_SCORE_SESSIONS, {
    variables: { playerAssessmentId },
    fetchPolicy: 'cache-and-network'
  });
  useLogQueryError(sessionsError, 'GetPlayerScoreSessions');

  const assessmentSessions: IAssessmentSession[] = useMemo(() => data?.playerAssessmentSessions || [], [data]);

  const user = getAuthUser();
  // Individual evaluator scores are Max-admin-only; the server refuses the detail query
  // to everyone else, so non-admins ask for submission presence instead.
  const { isAdmin, loading: adminLoading } = hasAdminAccess();
  const variables = {
    assessmentSessionId: activeAssessmentSessionId,
    companyId: user?.settings.activeCompanyId,
    playerAssessmentId
  };

  const { data: detailResult, error: detailError } = useQuery(GET_PLAYER_SCORES, {
    skip: !activeAssessmentSessionId || adminLoading || !isAdmin,
    variables,
    fetchPolicy: 'cache-and-network'
  });
  useLogQueryError(detailError, 'GetPlayerScores');

  const { data: submissionResult, error: submissionError } = useQuery(GET_PLAYER_SCORE_SUBMISSIONS, {
    skip: !activeAssessmentSessionId || adminLoading || isAdmin,
    variables,
    fetchPolicy: 'cache-and-network'
  });
  useLogQueryError(submissionError, 'GetPlayerScoreSubmissions');

  const sessionResult = isAdmin ? detailResult : submissionResult;

  const skills = useMemo(() => detailResult?.skills || [], [detailResult]);
  const scores = useMemo(() => detailResult?.assessmentSessionScores || [], [detailResult]);
  const submissions = useMemo(() => submissionResult?.assessmentSessionScoreSubmissions || [], [submissionResult]);
  const drills = useMemo(() => sessionResult?.drills || [], [sessionResult]);
  const members = useMemo(() => sessionResult?.allMembers || [], [sessionResult]);

  return <PlayerScoresContainer
    assessmentSessions={assessmentSessions}
    skills={skills}
    drills={drills}
    members={members}
    scores={scores}
    submissions={submissions}
    canViewScoreDetails={isAdmin}
    setActiveAssessmentSessionId={setActiveAssessmentSessionId}
  />;
};
